import {
	getTokenIcon,
	getTokenLogoFallback,
	normalizeTokenId,
	type ExtendedTokenId,
} from "@/lib/token-assets";
import { cn } from "@/lib/utils";

const plainSize = {
	sm: "h-5 w-5",
	md: "h-8 w-8",
	lg: "h-11 w-11",
} as const;

export function TokenIcon({
	token,
	size = "md",
	plain = false,
	className,
	label,
}: {
	token: ExtendedTokenId | string;
	size?: "sm" | "md" | "lg";
	plain?: boolean;
	className?: string;
	label?: string;
}) {
	const norm = normalizeTokenId(token) ?? token;
	const src = getTokenIcon(String(norm));
	const fallback = getTokenLogoFallback(String(norm));
	const alt = label ?? String(norm);
	const isBrand = norm === "USDM" || norm === "MMK";

	if (!src) {
		return (
			<span
				className={cn(
					"inline-flex items-center justify-center rounded-full bg-primary/10 font-display text-[10px] font-bold text-primary",
					plainSize[size],
					className,
				)}
				title={alt}
			>
				{String(norm).slice(0, 3)}
			</span>
		);
	}

	return (
		<span
			className={cn(
				isBrand && "brand-token-mark",
				plainSize[size],
				!plain && !isBrand && "rounded-full ring-2 ring-background",
				"inline-flex shrink-0 items-center justify-center overflow-hidden bg-background",
				className,
			)}
		>
			<img
				src={src}
				alt={alt}
				className={cn(plainSize[size], "object-contain p-0.5")}
				decoding="async"
				draggable={false}
				onError={(e) => {
					const el = e.currentTarget;
					if (el.getAttribute("data-fallback") === "1") {
						el.style.display = "none";
						return;
					}
					if (fallback) {
						el.setAttribute("data-fallback", "1");
						el.src = fallback;
					} else {
						el.style.display = "none";
					}
				}}
			/>
		</span>
	);
}

export function TokenIconRow({
	tokens,
	size = "sm",
	className,
}: {
	tokens: string[];
	size?: "sm" | "md";
	className?: string;
}) {
	return (
		<div className={cn("flex items-center -space-x-2", className)}>
			{tokens.map((t) => (
				<TokenIcon key={t} token={t} size={size} />
			))}
		</div>
	);
}
